import { createSignal, createEffect, onMount, onCleanup, on, } from "solid-js";
import { MapProvider } from "../MapProvider";
import { mapEvents } from "../../events";
import { vectorStyleList } from "../../mapStyles";
/** Creates a new Map Container */
export const MapGL = (props) => {
    let map;
    let mapRef;
    let resizeObserver;
    let mutationObserver;
    const [mapLoaded, setMapLoaded] = createSignal(null);
    const [darkMode, setDarkMode] = createSignal((typeof window !== "undefined" &&
        window.matchMedia("(prefers-color-scheme: dark)").matches) ||
        (typeof document !== "undefined" &&
            document.body.classList.contains("dark")));
    const [internal, setInternal] = createSignal(false);
    const debug = (text, value) => (props.debug || props.debugEvents) &&
        console.debug("%c[MapGL]", "color: #0ea5e9", text, value || "");
    const getStyle = (light, dark) => {
        const style = darkMode() && dark ? dark : light;
        return typeof style === "string" || style instanceof String
            ? style
                ?.split(":")
                .reduce((p, c) => p && p[c], vectorStyleList)
                ?.replace("{apikey}", 
            //@ts-ignore
            props.apikey || import.meta.env.VITE_VECTOR_API_KEY) || style
            : style;
    };
    onMount(async () => {
        let mapLib = props.mapLib || (await import("mapbox-gl"));
        if (!mapLib.Map)
            mapLib = window["maplibregl"] || window["mapboxgl"];
        if (typeof mapLib.supported === "function" && !mapLib.supported())
            throw new Error("Mapbox GL not supported");
        debug(`Map (v${mapLib.version}) loading...`);
        map = new mapLib.Map({
            accessToken: 
            //@ts-ignore
            props.options?.accessToken || import.meta.env.VITE_MAPBOX_ACCESS_TOKEN,
            interactive: props.options?.interactive || !!props.onViewportChange,
            ...props.options,
            ...props.viewport,
            projection: props.options?.projection,
            container: mapRef,
            style: getStyle(props.options?.style, props.darkStyle),
            fitBoundsOptions: { padding: props.viewport?.padding },
        });
        map.debug = props.debug;
        map.debugEvents = props.debugEvents;
        map.sourceIdList = [];
        map.layerIdList = [];
        window.MapLib = mapLib;
        // Hook up events
        mapEvents.forEach((item) => {
            const prop = props[item];
            if (prop) {
                const event = item.slice(2).toLowerCase();
                if (typeof prop === "function") {
                    map.on(event, (evt) => {
                        setTimeout(() => {
                            if (evt.clickOnLayer)
                                return;
                            prop(evt);
                            props.debugEvents && debug(`Map '${event}' event:`, evt);
                        }, 0);
                    });
                }
                else {
                    Object.keys(prop).forEach((layerId) => {
                        map.on(event, layerId, (evt) => {
                            setTimeout(() => {
                                if (evt.clickOnLayer)
                                    return;
                                prop[layerId](evt);
                                props.debugEvents &&
                                    debug(`Map '${event}' event on '${layerId}':`, evt);
                            }, 0);
                        });
                    });
                }
            }
        });
        map.once("load", () => {
            setMapLoaded(map);
            debug("Map loaded");
            // Handle User Interaction
            ["mousedown", "touchstart", "wheel"].forEach((event) => map.on(event, (evt) => !evt.rotate && props.onUserInteraction?.(true)));
            ["moveend", "mouseup", "touchend"].forEach((event) => map.on(event, (evt) => !evt.rotate && props.onUserInteraction?.(false)));
            // Listen to dark theme changes
            const darkTheme = typeof window !== "undefined" &&
                window?.matchMedia("(prefers-color-scheme: dark)");
            darkTheme?.addEventListener("change", () => {
                setDarkMode(darkTheme.matches);
                debug("Set dark theme to:", darkTheme.matches?.toString());
            });
            mutationObserver = new MutationObserver(() => {
                const darkTheme = document.body.classList.contains("dark");
                setDarkMode(darkTheme);
                debug("Set theme to:", darkTheme);
            });
            mutationObserver.observe(document.body, { attributes: true });
            // Listen to map container size changes
            if (!props.disableResize) {
                resizeObserver = new ResizeObserver(() => {
                    setTimeout(() => {
                        map?.resize();
                    }, 0);
                    debug("Map resized");
                });
                resizeObserver.observe(mapRef);
            }
            // Update Configuration
            createEffect(() => {
                for (const key in props.config) {
                    if (!key || key === "id")
                        continue;
                    const id = props.config?.id || "basemap";
                    const value = props.config[key];
                    //@ts-ignore
                    map?.setConfigProperty(id, key, value);
                    debug(`Set Config (${id}:${key}) to:`, value);
                }
            });
            // Update Viewport
            map.on("move", (event) => {
                const viewport = {
                    ...props.viewport,
                    id: props.id,
                    point: {
                        x: event.originalEvent?.x,
                        y: event.originalEvent?.y,
                    },
                    center: props.viewport?.center?.lat
                        ? map.getCenter()
                        : [map.getCenter().lng, map.getCenter().lat],
                    zoom: map.getZoom(),
                    pitch: map.getPitch(),
                    bearing: map.getBearing(),
                    inTransit: true,
                    bounds: null,
                    //   !internal()
                    //     ? props.viewport?.center?.lat
                    //       ? map.getBounds()
                    //       : [
                    //           [
                    //             map.getBounds().getNorthEast().lng,
                    //             map.getBounds().getNorthEast().lat,
                    //           ],
                    //           [
                    //             map.getBounds().getSouthWest().lng,
                    //             map.getBounds().getSouthWest().lat,
                    //           ],
                    //         ]
                    //     : null,
                };
                setInternal(true);
                !event.viewport && props.onViewportChange?.(viewport);
            });
            map.on("moveend", (event) => {
                !event.rotate &&
                    props.onViewportChange?.({ ...props.viewport, inTransit: false });
                setInternal(false);
            });
        });
    });
    // Hook up viewport event
    createEffect(on(() => props.viewport, (vp) => {
        if (props.id !== vp?.id || internal())
            return;
        const viewport = {
            ...vp,
            ...(vp.bounds
                ? map.cameraForBounds(vp.bounds, {
                    bearing: vp?.bearing,
                    pitch: vp?.pitch,
                    padding: vp?.padding,
                })
                : null),
        };
        map.stop()[props.transitionType || "flyTo"](viewport);
        debug(`Update Viewport (${props.transitionType || "flyTo"}):`, viewport);
    }, { defer: true }));
    // Update Projection
    createEffect(() => {
        const proj = props.options?.projection;
        if (!map || !proj)
            return;
        map.setProjection(proj);
        debug("Set Projection to:", proj);
    });
    // Update Cursor
    createEffect(() => {
        const cur = props.cursorStyle;
        if (!map || !cur)
            return;
        map.getCanvas().style.cursor = cur;
        debug("Set Cursor to:", cur);
    });
    const insertLayers = (list, layers) => {
        layers.forEach((layer) => {
            const index = list.findIndex((i) => layer.metadata.smg.beforeType
                ? i.type === layer.metadata.smg.beforeType
                : i.id === layer.metadata.smg.beforeId);
            list =
                index === -1
                    ? [...list, layer]
                    : [...list.slice(0, index), layer, ...list.slice(index + 1)];
        });
        return list;
    };
    // Update map style
    createEffect((prev) => {
        const style = getStyle(props.options?.style, props.darkStyle);
        if (map?.isStyleLoaded() && prev !== style) {
            const oldStyle = map.getStyle();
            const oldLayers = oldStyle.layers.filter((l) => map.layerIdList.includes(l.id));
            const oldSources = Object.keys(oldStyle.sources)
                .filter((s) => map.sourceIdList.includes(s))
                .reduce((obj, key) => ({ ...obj, [key]: oldStyle.sources[key] }), {});
            map.setStyle(style);
            map.once("styledata", () => {
                if (!oldLayers)
                    return;
                const newStyle = map.getStyle();
                map.setStyle({
                    ...newStyle,
                    sources: { ...newStyle.sources, ...oldSources },
                    layers: insertLayers(newStyle.layers, oldLayers),
                    fog: oldStyle.fog,
                    terrain: oldStyle.terrain,
                });
                debug("Set Mapstyle to:", style);
            });
        }
        return style;
    }, props.options?.style);
    // Update debug features
    [
        "showTileBoundaries",
        "showTerrainWireframe",
        "showCollisionBoxes",
        "showPadding",
        "showOverdrawInspector",
    ].forEach((item) => {
        createEffect(() => {
            const prop = props[item];
            if (!map || !prop)
                return;
            map[item] = prop;
            debug(`Set ${item} to:`, prop);
        });
    });
    onCleanup(() => {
        resizeObserver?.disconnect();
        mutationObserver?.disconnect();
        map?.remove();
        debug("Map removed");
    });
    return (<div ref={mapRef} id={props.id} class={props?.class} classList={props?.classList} style={props?.class || props?.classList
            ? null
            : props.style || {
                position: "absolute",
                inset: 0,
                "z-index": -1,
            }}>
      {mapLoaded() && (<MapProvider map={mapLoaded()}>
          <style>{`.overlay{position:relative;width:100%;height:100%;pointer-events:none}.overlay>*{pointer-events:auto}`}</style>
          <div class="overlay">{props.children}</div>
        </MapProvider>)}
    </div>);
};
